<template>
  <div class="p-t-m">
    <div class="m-b-md">
      <h1 class="form-section-title m-b-xs">Personal access tokens</h1>
      <p class="text-gray-700 m-b-sm">
        Personal access tokens let scripts and integrations read your team's data through the WrestlingIQ API as you. Treat them like passwords — don't share them, and revoke any you no longer need.
      </p>
      <p class="text-14 text-gray-600 m-b-none">
        <i class="fa-regular fa-circle-user m-r-xxs" aria-hidden="true"></i>
        New tokens will act as
        <strong>{{ activeProfileDisplayName }}</strong><span v-if="activeProfileTeamName"> at <strong>{{ activeProfileTeamName }}</strong></span>.
        Switch profiles in the navbar to mint a token under a different role.
      </p>
    </div>

    <div class="alert alert-warning m-b-md" role="alert">
      <div class="flex align-items-start gap-sm">
        <i class="fa-solid fa-flask text-18" aria-hidden="true" style="margin-top: 2px;"></i>
        <div class="flex-shrink-1 w-full">
          <p class="font-heavy m-b-xxs">Research preview</p>
          <p class="text-14 m-b-none">
            The public API is in early access. Use it at your own risk — endpoints and responses may change without notice. Tokens can <strong>read</strong> your team's data unless you limit them to specific areas below; they can only <strong>change</strong> data if you grant a write permission below, and only for writes a team admin has enabled for the whole team. Deleting data is never allowed through the API.
            Need to use this with an AI agent or want early access to the WrestlingIQ CLI?
            <a href="mailto:support@wrestlingiq.com?subject=WrestlingIQ%20CLI%20early%20access">Contact us</a>.
          </p>
        </div>
      </div>
    </div>

    <div v-if="newToken"
         class="alert alert-warning m-b-md"
         role="alert"
         aria-live="polite">
      <div class="flex align-items-start gap-sm">
        <i class="fa-solid fa-key text-18" aria-hidden="true" style="margin-top: 2px;"></i>
        <div class="flex-shrink-1 w-full">
          <p class="font-heavy m-b-xxs">Copy this token now</p>
          <p class="text-14 m-b-sm">
            You won't be able to see it again. Save it somewhere secure — a password manager or a CI secret store.
          </p>
          <div class="flex align-items-center gap-sm m-b-sm">
            <input
              ref="newTokenInput"
              type="text"
              class="form-control"
              :value="newToken.token"
              readonly
              aria-label="Personal access token"
              @focus="$event.target.select()"
            />
            <button type="button"
                    class="btn btn-primary"
                    :class="{ 'btn-success': copied }"
                    @click="copyNewToken">
              <i :class="copied ? 'fa-solid fa-check' : 'fa-solid fa-copy'" aria-hidden="true"></i>
              {{ copied ? 'Copied' : 'Copy' }}
            </button>
          </div>
          <a href="#" class="text-14" @click.prevent="dismissNewToken">I've saved it — dismiss</a>
        </div>
      </div>
    </div>

    <div role="form" class="m-t-lg m-b-lg">
      <h2 class="form-section-h2 m-b-sm">Create a new token</h2>
      <div class="form-group" style="max-width: 480px;">
        <label for="api_token_name">Token name</label>
        <input
          id="api_token_name"
          v-model="newName"
          name="token_name"
          type="text"
          class="form-control"
          placeholder="e.g. Monthly reporting cron"
          maxlength="100"
          autocomplete="off"
          @keyup.enter="createToken"
        />
        <small class="text-muted">A label so you can tell tokens apart later.</small>
      </div>
      <div class="form-group" style="max-width: 480px;">
        <label class="m-b-none">Read access</label>
        <p class="text-14 text-gray-600 m-b-sm">
          You can't change this later, only revoke and create a new token.
        </p>
        <div class="flex flex-col gap-y-sm">
          <label class="flex font-normal m-b-none">
            <input type="radio" name="read_access" :value="false" v-model="limitedReads">
            <div class="m-l-m flex flex-col">
              <span class="font-semi">Everything you can see</span>
              <span class="text-14 text-gray-600">For your own scripts and the WrestlingIQ CLI.</span>
            </div>
          </label>
          <label class="flex font-normal m-b-none">
            <input type="radio" name="read_access" :value="true" v-model="limitedReads">
            <div class="m-l-m flex flex-col">
              <span class="font-semi">Only the areas you choose</span>
              <span class="text-14 text-gray-600">Use this when you give a token to another company's product. It can't read anything you don't check.</span>
            </div>
          </label>
        </div>
        <div v-if="limitedReads" class="flex flex-col gap-y-sm m-t-sm m-l-lg">
          <label v-for="capability in readCapabilities" :key="capability.key" class="flex font-normal m-b-none">
            <input type="checkbox"
                   :value="capability.key"
                   :checked="readChecked(capability.key)"
                   :disabled="readImplied(capability.key)"
                   @change="toggleReadScope(capability.key, $event.target.checked)">
            <div class="m-l-m flex flex-col">
              <span class="font-semi">{{ capability.label }}</span>
              <span class="text-14 text-gray-600">{{ capability.description }}</span>
              <span v-if="readImplied(capability.key)" class="text-14 text-gray-600">
                Included because this token can also change it.
              </span>
            </div>
          </label>
        </div>
      </div>
      <div v-if="profileIsCoach" class="form-group" style="max-width: 480px;">
        <label class="m-b-none">Write permissions</label>
        <p class="text-14 text-gray-600 m-b-sm">
          Check any writes this token should be allowed to make — you can't change this later, only revoke and create a new token.
        </p>
        <div class="flex flex-col gap-y-sm">
          <label v-for="capability in writeCapabilities" :key="capability.key" class="flex font-normal m-b-none">
            <input type="checkbox"
                   :value="capability.key"
                   v-model="selectedScopes"
                   :disabled="!teamAllows(capability.key)">
            <div class="m-l-m flex flex-col">
              <span class="font-semi" :class="{ 'text-gray-500': !teamAllows(capability.key) }">{{ capability.label }}</span>
              <span class="text-14 text-gray-600">{{ capability.description }}</span>
              <span v-if="!teamAllows(capability.key)" class="text-14 text-yellow-800">
                Ask a team admin to enable this under Settings &rarr; Team Settings &rarr; Privacy &amp; Permissions &rarr; API Access.
              </span>
            </div>
          </label>
        </div>
      </div>
      <p v-else class="text-14 text-gray-600" style="max-width: 480px;">
        Tokens created for this profile are read-only. Write permissions are only available on coach tokens.
      </p>
      <div v-show="createError" class="m-b-sm">
        <small class="text-danger">{{ createErrorMessage }}</small>
      </div>
      <div>
        <ladda-button @lbClicked="createToken" el-class="btn-primary" :loading="creating">
          Create token
        </ladda-button>
      </div>
    </div>

    <div class="m-t-lg">
      <h2 class="form-section-h2 m-b-sm">Your tokens</h2>
      <div v-show="listError" class="m-b-sm">
        <small class="text-danger">{{ listErrorMessage }}</small>
      </div>
      <spinner v-if="loading"></spinner>
      <p v-else-if="tokens.length === 0 && !listError" class="text-gray-600 m-b-none">
        You don't have any active tokens yet. Create one above to get started.
      </p>
      <div v-else-if="tokens.length > 0" style="overflow-x: auto;">
        <table class="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Prefix</th>
              <th>Permissions</th>
              <th>Created</th>
              <th>Last used</th>
              <th><span class="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="token in tokens" :key="token.id">
              <td class="font-semi">{{ token.name }}</td>
              <td><code>{{ token.token_prefix }}…</code></td>
              <td>
                <span v-if="!token.limited_reads && (!token.scopes || token.scopes.length === 0)" class="text-14 text-gray-600">Read-only</span>
                <span v-else class="flex flex-wrap gap-x-xs gap-y-xs">
                  <span v-if="token.limited_reads"
                        class="bg-yellow-50 text-yellow-800 border border-solid border-yellow-200 rounded-full p-x-xs text-12 font-semi text-nowrap">
                    Limited reads
                  </span>
                  <span v-for="scope in token.scopes"
                        :key="scope"
                        class="rounded-full p-x-xs text-12 font-semi text-nowrap border border-solid"
                        :class="isReadScope(scope) ? 'bg-gray-50 text-gray-700 border-gray-200' : 'bg-blue-50 text-blue-700 border-blue-200'">
                    {{ scopeLabel(scope) }}
                  </span>
                </span>
              </td>
              <td class="text-gray-700">{{ formatDate(token.created_at) }}</td>
              <td class="text-gray-700">
                {{ token.last_used_at ? formatDate(token.last_used_at) : 'Never' }}
              </td>
              <td class="text-right">
                <button
                  type="button"
                  class="btn btn-default btn-xs text-red-600"
                  :disabled="revokingId === token.id"
                  @click="revokeToken(token)"
                >
                  {{ revokingId === token.id ? 'Revoking…' : 'Revoke' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import LaddaButton from '../../shared/ladda_button.vue';
import { API_READ_CAPABILITIES, API_WRITE_CAPABILITIES, impliedReadFor } from '../../shared/api_write_capabilities';

export default {
  name: 'personal-access-tokens-page',
  components: { LaddaButton },
  props: {
    activeProfileDisplayName: { type: String, default: '' },
    activeProfileTeamName: { type: String, default: '' },
    // Only coach tokens may carry write scopes (enforced server-side too)
    profileIsCoach: { type: Boolean, default: false },
    // Write capabilities the team admin has enabled; the rest render disabled
    teamWriteCapabilities: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      creating: false,
      revokingId: null,
      tokens: [],
      newName: '',
      selectedScopes: [],
      limitedReads: false,
      selectedReadScopes: [],
      writeCapabilities: API_WRITE_CAPABILITIES,
      readCapabilities: API_READ_CAPABILITIES,
      newToken: null,
      copied: false,
      createError: false,
      createErrorMessage: '',
      listError: false,
      listErrorMessage: '',
    };
  },
  computed: {
    // Reads the selected writes already grant (the server adds them too)
    impliedReadScopes() {
      return this.selectedScopes.map(impliedReadFor).filter(Boolean);
    },
  },
  mounted() {
    this.fetchTokens();
  },
  methods: {
    teamAllows(capabilityKey) {
      return this.teamWriteCapabilities.includes(capabilityKey);
    },
    readImplied(capabilityKey) {
      return this.impliedReadScopes.includes(capabilityKey);
    },
    readChecked(capabilityKey) {
      return this.readImplied(capabilityKey) || this.selectedReadScopes.includes(capabilityKey);
    },
    toggleReadScope(capabilityKey, checked) {
      const others = this.selectedReadScopes.filter((key) => key !== capabilityKey);
      this.selectedReadScopes = checked ? [...others, capabilityKey] : others;
    },
    isReadScope(scope) {
      return API_READ_CAPABILITIES.some((c) => c.key === scope);
    },
    scopeLabel(scope) {
      const write = API_WRITE_CAPABILITIES.find((c) => c.key === scope);
      if (write) return write.label;
      const read = API_READ_CAPABILITIES.find((c) => c.key === scope);
      return read ? `Read ${read.label.toLowerCase()}` : scope;
    },
    fetchTokens() {
      const vm = this;
      vm.loading = true;
      vm.listError = false;
      vm.listErrorMessage = '';
      axios.get(vm.$apiService.personalAccessTokensUrl())
        .then((response) => {
          vm.tokens = response.data.personal_access_tokens;
        })
        .catch(() => {
          vm.listError = true;
          vm.listErrorMessage = 'Could not load your tokens. Please refresh and try again.';
        })
        .finally(() => {
          vm.loading = false;
        });
    },
    createToken() {
      const vm = this;
      if (vm.creating) return;

      vm.createError = false;
      vm.createErrorMessage = '';

      if (vm.newName.trim().length === 0) {
        vm.createError = true;
        vm.createErrorMessage = 'A token name is required.';
        return;
      }

      const readScopes = vm.limitedReads ? vm.selectedReadScopes : [];
      if (vm.limitedReads && readScopes.length === 0 && vm.selectedScopes.length === 0) {
        vm.createError = true;
        vm.createErrorMessage = 'Choose at least one area this token can read.';
        return;
      }

      vm.creating = true;
      // A successful create renders the table, so clear any stale fetch/revoke
      // banner that would otherwise sit above it.
      vm.listError = false;
      vm.listErrorMessage = '';
      axios.post(vm.$apiService.personalAccessTokensUrl(), {
        personal_access_token: {
          name: vm.newName.trim(),
          limited_reads: vm.limitedReads,
          scopes: [...vm.selectedScopes, ...readScopes],
        },
      })
        .then((response) => {
          vm.newToken = response.data;
          vm.copied = false;
          vm.newName = '';
          vm.selectedScopes = [];
          vm.limitedReads = false;
          vm.selectedReadScopes = [];
          vm.tokens.unshift({
            id: response.data.id,
            name: response.data.name,
            token_prefix: response.data.token_prefix,
            created_at: response.data.created_at,
            updated_at: response.data.updated_at,
            last_used_at: response.data.last_used_at,
            revoked_at: response.data.revoked_at,
            scopes: response.data.scopes,
            limited_reads: response.data.limited_reads,
            profile: response.data.profile,
          });
        })
        .catch((error) => {
          vm.createError = true;
          const errors = error.response && error.response.data && error.response.data.errors;
          vm.createErrorMessage = (errors && errors.join(', ')) || 'Could not create token. Please try again.';
        })
        .finally(() => {
          vm.creating = false;
        });
    },
    revokeToken(token) {
      const vm = this;
      // eslint-disable-next-line no-alert
      const ok = window.confirm(`Revoke "${token.name}"? Any scripts using it will stop working immediately.`);
      if (!ok) return;

      vm.revokingId = token.id;
      vm.listError = false;
      vm.listErrorMessage = '';
      axios.delete(vm.$apiService.personalAccessTokenUrl(token.id))
        .then(() => {
          vm.tokens = vm.tokens.filter((t) => t.id !== token.id);
        })
        .catch(() => {
          vm.listError = true;
          vm.listErrorMessage = `Could not revoke "${token.name}". It is still active — please try again.`;
        })
        .finally(() => {
          vm.revokingId = null;
        });
    },
    copyNewToken() {
      const vm = this;
      if (!vm.newToken) return;
      const input = vm.$refs.newTokenInput;
      input.select();
      try {
        document.execCommand('copy');
        vm.copied = true;
      } catch (_e) {
        // Older browser fallback: leave selected so user can ctrl-c.
      }
    },
    dismissNewToken() {
      this.newToken = null;
      this.copied = false;
    },
    formatDate(iso) {
      if (!iso) return '';
      try {
        return new Date(iso).toLocaleString();
      } catch (_e) {
        return iso;
      }
    },
  },
};
</script>
